import React from 'react'
import { Helmet } from 'react-helmet'
import { helmetJsonLdProp } from 'react-schemaorg'
import { BreadcrumbList, CollectionPage } from 'schema-dts'
import { useTranslation } from 'gatsby-plugin-react-i18next'

import { CategoryWithCTA } from '../../types/category'
import { PostLink } from '../../types/landingPage'
import { OpenGraphSEO, TwitterSEO } from './OpenGraphSEO'
import useUrlPathBuilder from '../../hooks/useUrlPathBuilder'
import { insertHandlebarsTemplate } from '../../utils/insertHandlebarsTemplate'
import useHandlebarsContext from '../../hooks/useHandlebarsContext'
import NoIndex from './NoIndex'
import useSiteData from '../../hooks/useSiteData'
import AlternateHreflang from './AlternateHreflang'
import { urlPathJoin } from '../../utils/url'

interface Props {
  category: CategoryWithCTA
  categoryPosts?: PostLink[]
  shouldRenderNoIndex: boolean
}

const CategorySEO: React.FC<Props> = ({
  category,
  categoryPosts,
  shouldRenderNoIndex,
}) => {
  const { urlPathBuilder, blogPrefix, baseURL } = useUrlPathBuilder()
  const site = useSiteData()
  const canonicalURL =
    category.seo?.canonicalUrl ??
    urlPathBuilder([blogPrefix, 'category', category.slug], { baseURL })

  const handlebarsContext = useHandlebarsContext()
  const pageTitle =
    category.seo?.pageTitle ?? category.seo?.openGraphTitle ?? category.name
  const description = insertHandlebarsTemplate(
    category.seo?.description ?? category.seo?.openGraphDescription,
    handlebarsContext
  )
  const {
    i18n: { language },
  } = useTranslation()

  return (
    <>
      <Helmet
        script={[
          helmetJsonLdProp<CollectionPage>({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            '@id': urlPathBuilder([blogPrefix, 'category', category.slug], {
              baseURL,
              hash: 'webpage',
            }),
            url: canonicalURL,
            name: category.name,
            isPartOf: {
              '@id': urlPathBuilder([], {
                baseURL,
                hash: 'website',
              }),
            },
            description: insertHandlebarsTemplate(
              category.seo?.description ?? undefined,
              handlebarsContext
            ),
            breadcrumb: {
              '@id': urlPathBuilder([blogPrefix, 'category', category.slug], {
                baseURL,
                hash: 'breadcrumb',
              }),
            },
            potentialAction: [
              {
                '@type': 'ReadAction',
                target: [canonicalURL],
              },
            ],
            inLanguage: language,
            mainEntity: {
              '@type': 'ItemList',
              numberOfItems: categoryPosts?.length ?? 0,
              itemListElement: categoryPosts?.map((post, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                item: {
                  '@type': 'Article',
                  url: urlPathBuilder([blogPrefix, post.slug]),
                  name: post.title,
                  headline: post.title,
                  image: post.featuredImage?.file.url ?? undefined,
                  datePublished: post.publishedAt,
                  dateModified: post.updatedAt,
                  mainEntityOfPage: canonicalURL,
                  author: post.authors?.map((author) => ({
                    '@type': 'Person',
                    name: author.name,
                    image: author.picture?.file.url ?? undefined,
                  })),
                  publisher: {
                    '@id': urlPathBuilder([], {
                      baseURL,
                      hash: 'organization',
                    }),
                  },
                },
              })),
            },
          }),
          helmetJsonLdProp<BreadcrumbList>({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            '@id': urlPathBuilder([blogPrefix, 'category', category.slug], {
              baseURL,
              hash: 'breadcrumb',
            }),
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                item: {
                  '@type': 'ListItem',
                  '@id': urlPathBuilder([blogPrefix], {
                    baseURL,
                  }),
                  url: urlPathBuilder([blogPrefix], {
                    baseURL,
                  }),
                  name: 'Blog',
                },
              },
              {
                '@type': 'ListItem',
                position: 2,
                item: {
                  '@type': 'WebPage',
                  '@id': canonicalURL,
                  url: canonicalURL,
                  name: category.name,
                },
              },
            ],
          }),
        ]}
      >
        <title>{pageTitle}</title>
        {description && <meta name="description" content={description} />}
        <link rel="canonical" href={canonicalURL} />
        <link rel="alternate" href={canonicalURL} hrefLang={site.locale} />
      </Helmet>
      <AlternateHreflang
        slug={urlPathJoin([blogPrefix, 'category', category.slug])}
      />
      <OpenGraphSEO
        title={pageTitle}
        description={description}
        url={canonicalURL}
        seo={category.seo}
      />
      <TwitterSEO title={pageTitle} description={description} />
      {shouldRenderNoIndex && (
        <NoIndex seo={category.seo} slug={category.slug} />
      )}
    </>
  )
}

export default CategorySEO
